import type {
  AdminCreateMeetingResponse,
  AdminMeetingLinksResponse,
  AdminMeetingListResponse,
  AdminRecordingActionResponse,
  AdminRescheduleRequest,
  AdminSessionResponse,
  AdminStatsResponse,
  ApiErrorBody,
  CreateMeetingRequest,
  MeetingEventView,
  MeetingRecordingView,
  MeetingStatus,
  MeetingView,
} from '@hiredude/meet-shared';

import { MeetApiError } from '@/lib/api';

const BASE = (import.meta.env.VITE_MEET_API_URL ?? 'http://localhost:4000').replace(/\/+$/, '');

/** Generous enough for a CSV export page over mobile data, short enough to act on. */
const REQUEST_TIMEOUT_MS = 20_000;

/**
 * Admin transport.
 *
 * Separate from `lib/api.ts` for one reason that matters: every request here
 * sends `credentials: 'include'` so the httpOnly session cookie travels, while
 * the join endpoints stay deliberately cookie-free. No token is ever held in
 * JavaScript, so there is nothing here for a stray script to read.
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      ...init,
      credentials: 'include',
      // `fetch` only rejects when the connection fails outright. A wedged API,
      // or a proxy that accepts the socket and then never answers, leaves the
      // request pending forever — a spinner with no way out. Aborting also
      // releases the connection, which a timeout at the call site does not.
      signal: init?.signal ?? AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      headers: { 'content-type': 'application/json', ...init?.headers },
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'TimeoutError') {
      throw new MeetApiError(0, 'timeout', 'The server took too long to respond. Try again.');
    }
    throw new MeetApiError(0, 'network_error', 'Could not reach the server. Check your connection.');
  }

  if (!response.ok) {
    let body: ApiErrorBody | undefined;
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      // Non-JSON error body; fall through to the generic message.
    }
    throw new MeetApiError(
      response.status,
      body?.error.code ?? 'http_error',
      body?.error.message ?? `Request failed with status ${response.status}`,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/* -------------------------------- session -------------------------------- */

export function adminLogin(email: string, password: string): Promise<AdminSessionResponse> {
  return request('/v1/admin/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export function adminLogout(): Promise<{ ok: true }> {
  return request('/v1/admin/logout', { method: 'POST' });
}

export function adminSession(): Promise<Pick<AdminSessionResponse, 'email'>> {
  return request('/v1/admin/session');
}

/* --------------------------------- data ---------------------------------- */

export interface MeetingListParams {
  status?: MeetingStatus[];
  q?: string;
  from?: string;
  to?: string;
  sort?: 'startsAt' | 'createdAt';
  direction?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export function adminStats(): Promise<AdminStatsResponse> {
  return request('/v1/admin/stats');
}

export function adminMeetings(params: MeetingListParams): Promise<AdminMeetingListResponse> {
  const search = new URLSearchParams();
  // Repeated `status` keys, which is what the server's array schema expects.
  for (const status of params.status ?? []) search.append('status', status);
  if (params.q) search.set('q', params.q);
  if (params.from) search.set('from', params.from);
  if (params.to) search.set('to', params.to);
  if (params.sort) search.set('sort', params.sort);
  if (params.direction) search.set('direction', params.direction);
  if (params.page) search.set('page', String(params.page));
  if (params.pageSize) search.set('pageSize', String(params.pageSize));

  const query = search.toString();
  return request(`/v1/admin/meetings${query ? `?${query}` : ''}`);
}

export function adminMeeting(id: string): Promise<MeetingView> {
  return request(`/v1/admin/meetings/${encodeURIComponent(id)}`);
}

export function adminMeetingEvents(id: string): Promise<{ events: MeetingEventView[] }> {
  return request(`/v1/admin/meetings/${encodeURIComponent(id)}/events`);
}

export type AdminChatMessage = {
  id: string;
  identity: string;
  role: string;
  name: string;
  text: string;
  at: string;
};

export function adminMeetingChat(id: string): Promise<{ messages: AdminChatMessage[] }> {
  return request(`/v1/admin/meetings/${encodeURIComponent(id)}/chat`);
}

export function adminMeetingRecordings(
  id: string,
): Promise<{ recordings: MeetingRecordingView[] }> {
  return request(`/v1/admin/meetings/${encodeURIComponent(id)}/recordings`);
}

export function adminMeetingLinks(id: string): Promise<AdminMeetingLinksResponse> {
  return request(`/v1/admin/meetings/${encodeURIComponent(id)}/links`);
}

export function adminCancelMeeting(id: string, reason?: string): Promise<MeetingView> {
  return request(`/v1/admin/meetings/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    body: JSON.stringify({ reason }),
  });
}

export function adminCreateMeeting(
  input: CreateMeetingRequest,
): Promise<AdminCreateMeetingResponse> {
  return request('/v1/admin/meetings', { method: 'POST', body: JSON.stringify(input) });
}

export function adminRescheduleMeeting(
  id: string,
  input: AdminRescheduleRequest,
): Promise<MeetingView> {
  return request(`/v1/admin/meetings/${encodeURIComponent(id)}/reschedule`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function adminStartRecording(id: string): Promise<AdminRecordingActionResponse> {
  return request(`/v1/admin/meetings/${encodeURIComponent(id)}/recording/start`, {
    method: 'POST',
  });
}

export function adminStopRecording(id: string): Promise<AdminRecordingActionResponse> {
  return request(`/v1/admin/meetings/${encodeURIComponent(id)}/recording/stop`, {
    method: 'POST',
  });
}
